/**
 * 系统设置 · 集成页：第三方服务集成卡片网格（设计稿 settings.jsx §INTEGRATIONS 1:1）。
 * adapty 真读 useAdaptyCredentials + AdaptyConfigDialog 真配置，其余卡据实标已接入/待接入/未启用，不编造连接态。
 * PayerMax 卡读框架通道配置列表，「配置」进入独立子页 /settings/integrations/payment-providers/PAYERMAX。
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePaymentProviderConfigs } from '~/data/payment-provider-config-queries';
import {
  useAdaptyCredentials,
  useBailianCredential,
  useDeeplCredential,
  useHuggingFaceCredential,
  useOllamaCredential,
  useOpenRouterCredential,
  useStripeCheckoutConfigs,
} from '~/data/queries';
import { Icons } from '~/lib/icons';
import { AdaptyConfigDialog } from './dialogs/AdaptyConfigDialog';
import { BailianConfigDialog } from './dialogs/BailianConfigDialog';
import { DeepLConfigDialog } from './dialogs/DeepLConfigDialog';
import { HuggingFaceConfigDialog } from './dialogs/HuggingFaceConfigDialog';
import { OllamaConfigDialog } from './dialogs/OllamaConfigDialog';
import { OpenRouterConfigDialog } from './dialogs/OpenRouterConfigDialog';
import { StripeCheckoutConfigDialog } from './dialogs/StripeCheckoutConfigDialog';
import { NOT_IN_SCOPE_TITLE, SectionHead } from './settings-shared';

// ---------- Integrations ----------
// 设计稿 settings.jsx §INTEGRATIONS 8 卡网格 1:1 还原（admin 侧可只读派生/承载的静态元数据，非 C 端表 → 真 gap）。
// 状态据实，不编造：
//   adapty  = 已接入（useAdaptyCredentials 真读，linked = `${creds.length} 个 App`，配置走 AdaptyConfigDialog 真路由）
//   deepl   = 据真（useDeeplCredential().configured：已接入/待接入；配置走 DeepLConfigDialog 真路由，第二位紧随 adapty）
//   neon/expo = 已接入（运营基础设施真接入，linked 据实）
//   stripe  = 据真（useStripeCheckoutConfigs：有 is_active 配置 → 已接入 / 否则待接入；
//             配置走 StripeCheckoutConfigDialog 真路由，ADM-PAY-S007）
//   payermax = 据真（usePaymentProviderConfigs：有 is_active 配置 → 已接入 / 否则待接入；
//             读取中或读取失败 → 中性「状态未知」，不当成未配置；配置进独立子页，ADM-PAY-S012）
//   slack/ga = 未启用（原型 off:true 即此，可选启用）
type IntegrationStatus = '已接入' | '待接入' | '未启用' | '状态未知';
interface IntegrationMeta {
  id: string;
  name: string;
  cat: string;
  desc: string;
  status: IntegrationStatus;
  linked: string;
  /** 颜色档位：复用 --chart-* token，不用品牌字面色值 */
  color: string;
  site?: string;
}

const INTEGRATIONS: IntegrationMeta[] = [
  {
    id: 'huggingface',
    name: 'Hugging Face',
    cat: 'AI 模型',
    desc: 'Inference Providers 路由或专用推理端点 · Access Token 鉴权；审核与脱敏分别选择模型',
    status: '待接入',
    linked: '',
    color: 'var(--chart-4)',
    site: 'https://huggingface.co/settings/tokens',
  },
  {
    id: 'ollama',
    name: 'Ollama',
    cat: 'AI 模型',
    desc: '本地模型经 Cloudflare Tunnel 连接 · 使用 Access 服务令牌鉴权；审核与脱敏分别选择模型',
    status: '待接入',
    linked: '',
    color: 'var(--chart-3)',
    site: 'https://ollama.com/',
  },
  {
    id: 'bailian',
    name: '阿里云百炼',
    cat: 'AI 模型',
    desc: '广告落地页 AI 审核与脱敏 · 独立 API Key；模型与提示词在大模型配置中维护',
    status: '待接入',
    linked: '',
    color: 'var(--chart-1)',
    site: 'https://bailian.console.aliyun.com/',
  },
  {
    id: 'openrouter',
    name: 'OpenRouter',
    cat: 'AI 模型',
    desc: '广告落地页 AI 审核与脱敏 · 全局 API Key；模型与提示词在大模型配置中维护',
    status: '待接入',
    linked: '',
    color: 'var(--chart-5)',
    site: 'https://openrouter.ai/settings/keys',
  },
  {
    id: 'adapty',
    name: 'Adapty',
    cat: '支付供应商',
    // server-side API 只同步 placement（Export）；产品目录基础信息（product_id/title）后续经 server-side getPaywall 镜像，
    // vendor_product_id / access_level_id / coin·bonus 等全部后台手动录入（getPaywall 拿不到 vendor id，见支付产品页）
    desc: '按 App 配置 Secret API Key，服务端同步 placement；产品与业务属性后台手动录入',
    status: '已接入',
    linked: '', // 运行时按 creds.length 填充
    color: 'var(--chart-1)',
    site: 'https://app.adapty.io',
  },
  {
    id: 'deepl',
    name: 'DeepL',
    cat: '翻译',
    desc: '全站多语言录入 / 书籍文案 / 用户反馈翻译（全局 Key）',
    status: '已接入',
    linked: '', // 运行时按 useDeeplCredential().configured 填充
    color: 'var(--chart-6)',
    site: 'https://www.deepl.com/pro-api',
  },
  {
    id: 'stripe',
    name: 'Stripe',
    cat: '支付渠道',
    // 本卡只管 Account / 凭据 / 回跳与启停；产品、价格、周期仍由 Adapty 管理（A8 §9 边界）
    desc: '按 App 配置 Stripe 账号与凭据，直接发起 Checkout；原生商品由 Adapty 管理，Web 使用独立目录',
    status: '待接入',
    linked: '', // 运行时按 useStripeCheckoutConfigs 填充
    color: 'var(--chart-2)',
    site: 'https://dashboard.stripe.com',
  },
  {
    id: 'payermax',
    name: 'PayerMax',
    cat: '支付渠道',
    // 框架通道（A8 §10）：凭据按 App、回调按商户账户组；原生商品价格在支付产品中单独配置
    desc: '框架通道 · 按 App 配置商户凭据与原生回跳，按商户账户登记回调地址；原生商品价格在支付产品中单独配置',
    status: '待接入',
    linked: '', // 运行时按 usePaymentProviderConfigs 填充
    color: 'var(--chart-4)',
    site: 'https://www.payermax.com',
  },
  {
    id: 'neon',
    name: 'Neon DB',
    cat: '数据库',
    desc: '运营数据后台直连',
    status: '已接入',
    linked: 'eu-central-1',
    color: 'var(--chart-3)',
    site: 'https://console.neon.tech',
  },
  {
    id: 'expo',
    name: 'Expo Push',
    cat: '推送',
    desc: 'iOS + Android 推送服务',
    status: '已接入',
    linked: '全部 App',
    color: 'var(--chart-5)',
    site: 'https://expo.dev',
  },
  {
    id: 'slack',
    name: 'Slack',
    cat: '协作通知',
    desc: '告警 / 日报通知',
    status: '未启用',
    linked: '—',
    color: 'var(--chart-2)',
    site: 'https://slack.com/apps',
  },
  {
    id: 'ga',
    name: 'Google Analytics',
    cat: '分析',
    desc: '客户端事件分析',
    status: '未启用',
    linked: '—',
    color: 'var(--chart-4)',
    site: 'https://analytics.google.com',
  },
];

/** 状态 → chip 配色 + 文案（● 前缀仅已接入态）*/
const INTEGRATION_STATUS_META: Record<IntegrationStatus, { chip: string; text: string }> = {
  已接入: { chip: 'chip-pos', text: '● 已接入' },
  待接入: { chip: 'chip-warn', text: '待接入' },
  未启用: { chip: '', text: '未启用' },
  状态未知: { chip: '', text: '状态未知' },
};

export default function IntegrationsPage() {
  const [huggingFaceOpen, setHuggingFaceOpen] = useState(false);
  const huggingFace = useHuggingFaceCredential();
  const [ollamaOpen, setOllamaOpen] = useState(false);
  const ollama = useOllamaCredential();
  const [bailianOpen, setBailianOpen] = useState(false);
  const bailian = useBailianCredential();
  const [openrouterOpen, setOpenrouterOpen] = useState(false);
  const openrouter = useOpenRouterCredential();
  const [open, setOpen] = useState(false);
  const [deeplOpen, setDeeplOpen] = useState(false);
  const [stripeOpen, setStripeOpen] = useState(false);
  const { data: creds } = useAdaptyCredentials();
  const { data: deepl } = useDeeplCredential();
  const { data: stripeConfigs } = useStripeCheckoutConfigs();
  const payermax = usePaymentProviderConfigs('PAYERMAX');
  const navigate = useNavigate();
  const payermaxConfigs = payermax.data ?? [];
  const payermaxActiveCount = payermaxConfigs.filter((c) => c.is_active).length;
  const linkedCount = creds.length;
  const deeplConfigured = deepl.configured;
  // 「已接入」以**已启用**为准：存在配置但全未启用时 C 端仍发不起 Checkout，不能报「已接入」
  const stripeActiveCount = stripeConfigs.filter((c) => c.is_active).length;
  return (
    <>
      <SectionHead
        title="集成"
        sub={`${INTEGRATIONS.length} 项 · 第三方服务连接`}
        action={
          // 浏览集成市场：admin 无集成市场端点（设计稿本即占位提示）→ disabled（§五），不假成功 toast
          <button
            className="btn btn-sm"
            disabled
            title={`${NOT_IN_SCOPE_TITLE} · 暂无集成市场端点`}
            style={{ opacity: 0.55, cursor: 'not-allowed' }}
          >
            <Icons.IconLock size={13} />
            浏览集成市场
          </button>
        }
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: 12,
        }}
      >
        {INTEGRATIONS.map((it) => {
          const isHuggingFace = it.id === 'huggingface';
          const isOllama = it.id === 'ollama';
          const isBailian = it.id === 'bailian';
          const isOpenRouter = it.id === 'openrouter';
          const isAdapty = it.id === 'adapty';
          const isDeepl = it.id === 'deepl';
          const isStripe = it.id === 'stripe';
          const isPayermax = it.id === 'payermax';
          const hasConfig =
            isPayermax ||
            isHuggingFace ||
            isOllama ||
            isBailian ||
            isOpenRouter ||
            isAdapty ||
            isDeepl ||
            isStripe;
          const openConfig = isPayermax
            ? () => navigate('/settings/integrations/payment-providers/PAYERMAX')
            : isHuggingFace
              ? () => setHuggingFaceOpen(true)
              : isOllama
                ? () => setOllamaOpen(true)
                : isBailian
                  ? () => setBailianOpen(true)
                  : isOpenRouter
                    ? () => setOpenrouterOpen(true)
                    : isAdapty
                      ? () => setOpen(true)
                      : isDeepl
                        ? () => setDeeplOpen(true)
                        : () => setStripeOpen(true);
          // DeepL / Stripe 状态据真：DeepL 看 configured；Stripe 看**已启用**配置数（未启用 = 待接入）
          const effStatus: IntegrationStatus = isPayermax
            ? !payermax.isSuccess
              ? '状态未知'
              : payermaxActiveCount > 0
                ? '已接入'
                : '待接入'
            : isHuggingFace
              ? huggingFace.data?.configured
                ? '已接入'
                : '待接入'
              : isOllama
                ? ollama.data?.configured
                  ? '已接入'
                  : '待接入'
                : isBailian
                  ? bailian.data?.configured
                    ? '已接入'
                    : '待接入'
                  : isOpenRouter
                    ? openrouter.data?.configured
                      ? '已接入'
                      : '待接入'
                    : isDeepl
                      ? deeplConfigured
                        ? '已接入'
                        : '待接入'
                      : isStripe
                        ? stripeActiveCount > 0
                          ? '已接入'
                          : '待接入'
                        : it.status;
          const linked = isPayermax
            ? payermax.isError
              ? '读取失败'
              : !payermax.isSuccess
                ? '读取中…'
                : payermaxConfigs.length === 0
                  ? '未配置'
                  : `${payermaxActiveCount}/${payermaxConfigs.length} 个 App 已启用`
            : isHuggingFace
              ? huggingFace.isError
                ? '读取失败'
                : huggingFace.data?.configured
                  ? 'Token 已配置'
                  : '未配置'
              : isOllama
                ? ollama.isError
                  ? '读取失败'
                  : ollama.data?.configured
                    ? 'Tunnel 与 Access 凭据已配置'
                    : '未配置'
                : isBailian
                  ? bailian.isError
                    ? '读取失败'
                    : bailian.data?.configured
                      ? 'API Key 已配置'
                      : '未配置'
                  : isOpenRouter
                    ? openrouter.isError
                      ? '读取失败'
                      : openrouter.data?.configured
                        ? 'API Key 已配置'
                        : '未配置'
                    : isAdapty
                      ? `${linkedCount} 个 App`
                      : isDeepl
                        ? deeplConfigured
                          ? '已配置'
                          : '未配置'
                        : isStripe
                          ? stripeConfigs.length === 0
                            ? '未配置'
                            : `${stripeActiveCount}/${stripeConfigs.length} 个 App 已启用`
                          : it.linked;
          const statusMeta = INTEGRATION_STATUS_META[effStatus];
          const dimmed = effStatus === '未启用';
          return (
            <div key={it.id} className="card" style={{ padding: 14 }}>
              <div className="hstack" style={{ gap: 10, marginBottom: 10 }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: it.color,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'var(--font-mono)',
                    fontWeight: 700,
                    fontSize: 14,
                    flexShrink: 0,
                    opacity: dimmed ? 0.5 : 1,
                  }}
                >
                  {it.name.slice(0, 1).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="hstack" style={{ gap: 6 }}>
                    <span style={{ fontWeight: 500, fontSize: 13 }}>{it.name}</span>
                    <span className={`chip ${statusMeta.chip}`} style={{ fontSize: 9.5 }}>
                      {statusMeta.text}
                    </span>
                  </div>
                  <div
                    className="mono"
                    style={{
                      fontSize: 10,
                      color: isPayermax && payermax.isError ? 'var(--neg)' : 'var(--fg-faint)',
                    }}
                  >
                    {it.cat} · {linked}
                  </div>
                </div>
                {hasConfig && (
                  <button
                    className="btn btn-ghost btn-icon"
                    style={{ width: 26, height: 26 }}
                    title={`配置 ${it.name}`}
                    onClick={openConfig}
                  >
                    <Icons.IconCog size={12} />
                  </button>
                )}
              </div>
              <div
                style={{
                  fontSize: 11.5,
                  color: 'var(--fg-muted)',
                  lineHeight: 1.55,
                  marginBottom: 10,
                }}
              >
                {isPayermax && payermax.isError
                  ? '配置列表读取失败，不当成「未配置」。进入配置页可重试。'
                  : it.desc}
              </div>
              <div className="hstack" style={{ gap: 6 }}>
                {hasConfig ? (
                  <button className="btn btn-sm" style={{ flex: 1 }} onClick={openConfig}>
                    配置
                  </button>
                ) : (
                  <button
                    className="btn btn-sm"
                    style={{ flex: 1 }}
                    disabled
                    title={NOT_IN_SCOPE_TITLE}
                  >
                    {effStatus === '未启用' ? '连接' : '配置'}
                  </button>
                )}
                {it.site && (
                  <a
                    className="btn btn-sm btn-ghost"
                    href={it.site}
                    target="_blank"
                    rel="noreferrer"
                    title={`访问 ${it.name} 官网`}
                  >
                    <Icons.IconExternal size={11} />
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {huggingFaceOpen && <HuggingFaceConfigDialog onClose={() => setHuggingFaceOpen(false)} />}
      {ollamaOpen && <OllamaConfigDialog onClose={() => setOllamaOpen(false)} />}
      {bailianOpen && <BailianConfigDialog onClose={() => setBailianOpen(false)} />}
      {openrouterOpen && <OpenRouterConfigDialog onClose={() => setOpenrouterOpen(false)} />}
      {open && <AdaptyConfigDialog onClose={() => setOpen(false)} />}
      {deeplOpen && <DeepLConfigDialog onClose={() => setDeeplOpen(false)} />}
      {stripeOpen && <StripeCheckoutConfigDialog onClose={() => setStripeOpen(false)} />}
    </>
  );
}
